<template>
  <div :class="wrapperClassName">
    <label :class="labelClassName" :for="idInput" v-dompurify-html="labelText"></label>

    <span class="help-trigger" title="Hilfe anzeigen" @click="toggleHelpText" v-if="help">
      <font-awesome-icon icon="question-circle" size="lg" />
    </span>
    <Message
      v-show="shouldShowHelp"
      closable
      severity="info"
      role="alert"
      class="mb-2 form-info-message"
      @close="toggleHelpText"
    >
      <span v-dompurify-html="help"></span>
    </Message>

    <input
      :id="idInput"
      :data-vv-as="labelText"
      :name="name"
      :required="required"
      :aria-invalid="showError || hasServerError"
      :aria-describedby="idErrorText"
      class="form-control"
      type="file"
      :accept="accepts"
      @change="onFileChosen"
      tabindex="0"
    />
    <span :id="idErrorText" v-show="showError" aria-atomic="true" class="text-danger form-error-message">
      {{ error || help }}
    </span>
    <span :id="idErrorText" v-show="hasServerError" aria-atomic="true" class="text-danger form-error-message">
      {{ serverError }}
    </span>
  </div>
</template>

<script>
import useToggler from '@/composables/general/useToggler';
import { toRefs, computed, ref } from '@vue/reactivity';
import Message from 'primevue/message';

export default {
  name: 'form-control-file',

  components: { Message },

  props: {
    name: {
      required: true,
      type: String,
    },
    label: {
      required: false,
      type: String,
      default: '',
    },
    required: {
      required: false,
      type: Boolean,
      default: false,
    },
    error: {
      required: false,
      type: String,
      default: '',
    },
    serverError: {
      required: false,
      type: String,
      default: '',
    },
    help: {
      required: false,
      type: String,
      default: '',
    },
    accepts: {
      required: false,
      type: String,
      default: '',
      // example: accepts="image/*,.pdf,.doc,.odt,.docx"
    },
    fileSizeLimitByte: {
      required: false,
      type: Number,
      default: 0,
      // 0 means no limit
    },
    additionalLabelClasses: {
      required: false,
      type: String,
      default: '',
    },
    prefixId: {
      required: false,
      type: String,
      default: 'wdw-bms-attr',
    },
  },

  emits: ['validate'],

  setup(props, context) {
    const { name, label, required, serverError, additionalLabelClasses, prefixId, fileSizeLimitByte } = toRefs(props);
    const { toggleValue: shouldShowHelp, toggle: toggleHelpText } = useToggler();
    const showError = ref(false);
    const value = ref(null);

    //+-------------------------+
    //|     Other methods       |
    //+-------------------------+
    function onFileChosen(event) {
      /**
       * Note: For some reason, vee-validate 4's validation using composition API doesn't trigger its validation method
       *  unlike other form controls. Since we don't want to mix with vee-validate's non-composition API, we use traditional
       *  method of change event
       *
       * The change event also emits "validate"-event to parent component.
       */
      const field = event.target;
      const uploadedFile = {
        validated: true,
        name: name.value,
        type: 'file',
        value: null,
        valueHuman: null,
      };

      if (field.files && field.files[0]) {
        const f = field.files[0];

        //validation
        const validated = fileSizeLimitByte.value <= 0 || f.size < fileSizeLimitByte.value;
        showError.value = !validated;

        if (validated) {
          uploadedFile.value = f;
          uploadedFile.valueHuman = {
            name: f.name,
            type: f.type,
            size: f.size,
          };
        } else {
          field.value = '';
        }

        context.emit('validate', uploadedFile);
      } else {
        uploadedFile.validated = !required.value;
        showError.value = !uploadedFile.validated;
        value.value = uploadedFile;
        // Emit event
        context.emit('validate', uploadedFile);
      }
    }

    //+-------------------------+
    //|     Computed            |
    //+-------------------------+
    const hasServerError = computed(() => {
      return serverError.value != undefined && serverError.value !== '';
    });
    const wrapperClassName = computed(() => {
      return {
        'form-group': true,
        'has-error': showError.value || hasServerError.value,
      };
    });
    const labelClassName = computed(() => {
      return additionalLabelClasses.value + ' ' + (showError.value || hasServerError.value ? 'text-danger' : '');
    });
    const labelText = computed(() => {
      return label.value + (required.value ? '*' : '');
    });
    const idInput = computed(() => {
      return prefixId.value + '-file-' + name.value;
    });
    const idErrorText = computed(() => {
      return prefixId.value + '-file-' + name.value + '-error';
    });

    return {
      // Computed
      wrapperClassName,
      labelClassName,
      idInput,
      idErrorText,
      hasServerError,
      labelText,
      // Methods
      toggleHelpText,
      // Events
      onFileChosen,
      // VARS
      shouldShowHelp,
      showError,
      value,
    };
  },
};
</script>

<style lang="scss" scoped>
label {
  display: inline;
  font-weight: bold;
  max-width: 100%;
}
input {
  margin-top: 5px;
}
.form-group {
  margin-bottom: 15px;
}
.help-trigger {
  cursor: pointer;
  margin-left: 4px;
}
</style>
